import * as React from 'react';
import {
    Avatar,
    Box,
    FormControlLabel,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { getMatchBettingStats } from '../../Api';
import {
    MatchBettingStatsDto,
    MatchBettingLeaderboardEntryDto,
    MatchBettingPopularBetDto,
} from '../../models/dtos/MatchBettingStatsDto';
import { CenteredProgress, EmptyState } from './MatchAdminTabCommon';

function formatBc(value?: number | null): string {
    if (value == null || !Number.isFinite(value)) {
        return '–';
    }
    return Math.round(value).toLocaleString();
}

function formatSignedBc(value?: number | null): string {
    if (value == null || !Number.isFinite(value)) {
        return '–';
    }
    const rounded = Math.round(value);
    const formatted = Math.abs(rounded).toLocaleString();
    if (rounded > 0) {
        return `+${formatted}`;
    }
    if (rounded < 0) {
        return `-${formatted}`;
    }
    return formatted;
}

function formatPct(value?: number | null): string {
    if (value == null || !Number.isFinite(value)) {
        return '–';
    }
    return `${value}%`;
}

function formatOdds(value?: number | null): string {
    if (value == null || !Number.isFinite(value)) {
        return '–';
    }
    return value.toFixed(2);
}

function UserNameWithAvatar(props: {
    name?: string | null;
    photoUrl?: string | null;
    size?: number;
}) {
    const label = props.name?.trim() || '–';
    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Avatar
                src={props.photoUrl?.trim() || undefined}
                alt={label}
                sx={{ width: props.size ?? 24, height: props.size ?? 24 }}
            >
                {label.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="inherit" noWrap>
                {label}
            </Typography>
        </Stack>
    );
}

function Kpi(props: { label: string; value: string; emphasize?: boolean }) {
    return (
        <Box sx={{ minWidth: 140, flex: '1 1 140px' }}>
            <Typography variant="caption" color="text.secondary" display="block">
                {props.label}
            </Typography>
            <Typography variant="subtitle1" fontWeight={props.emphasize ? 700 : 600}>
                {props.value}
            </Typography>
        </Box>
    );
}

function AwardCard(props: {
    title: string;
    name: string;
    detail: string;
    betName?: string | null;
    photoUrl?: string | null;
}) {
    return (
        <Box
            sx={{
                flex: '1 1 220px',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
            }}
        >
            <Typography variant="caption" color="text.secondary" display="block">
                {props.title}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
                <UserNameWithAvatar name={props.name} photoUrl={props.photoUrl} size={28} />
            </Box>
            {props.betName ? (
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                    Bet: {props.betName}
                </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
                {props.detail}
            </Typography>
        </Box>
    );
}

function popularBetLabel(bet: MatchBettingPopularBetDto): string {
    const parts: string[] = [];
    if (bet.bet_type) {
        parts.push(bet.bet_type);
    }
    if (bet.game_type && bet.game_type !== 'Regular Time') {
        parts.push(bet.game_type);
    }
    if (bet.period && bet.period !== 'Full match') {
        parts.push(bet.period);
    }
    return parts.join(' · ') || '–';
}

export default function MatchBettingResultsTab(props: {
    matchId: number;
    onError?: (message: string) => void;
}) {
    const { matchId, onError } = props;
    const [loading, setLoading] = React.useState(true);
    const [excludeWhales, setExcludeWhales] = React.useState(false);
    const [stats, setStats] = React.useState<MatchBettingStatsDto | null>(null);
    const requestSeq = React.useRef(0);

    React.useEffect(() => {
        if (matchId <= 0) {
            setStats(null);
            setLoading(false);
            return;
        }

        const seq = ++requestSeq.current;
        let cancelled = false;
        setLoading(true);

        getMatchBettingStats(matchId, excludeWhales)
            .then(result => {
                if (cancelled || seq !== requestSeq.current) {
                    return;
                }
                setStats(result);
            })
            .catch(() => {
                if (cancelled || seq !== requestSeq.current) {
                    return;
                }
                setStats(null);
                onError?.('Failed to load betting results');
            })
            .finally(() => {
                if (!cancelled && seq === requestSeq.current) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [matchId, excludeWhales, onError]);

    if (loading) {
        return <CenteredProgress />;
    }

    if (stats == null) {
        return <EmptyState message="Betting results are not ready for this match yet. Stats are computed after settlement." />;
    }

    const overview = stats.overview;
    const whales = stats.whales;
    const leaderboard = stats.leaderboard ?? [];
    const popularBets = stats.popular_bets ?? [];
    const king = stats.king_of_match;
    const bestRoi = stats.best_roi;
    const biggestWin = stats.awards?.biggest_single_win;
    const longestOdds = stats.awards?.longest_odds_winner;

    return (
        <Stack spacing={3} sx={{ width: '100%', py: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Box>
                    <Typography variant="h6">Betting Results</Typography>
                    {stats.match && (
                        <Typography variant="body2" color="text.secondary">
                            {[stats.match.home, stats.match.away].filter(Boolean).join(' vs ')}
                            {stats.match.score ? ` · ${stats.match.score}` : ''}
                            {stats.timestamp ? ` · computed ${stats.timestamp}` : ''}
                        </Typography>
                    )}
                </Box>
                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeWhales}
                            onChange={(_, checked) => setExcludeWhales(checked)}
                        />
                    }
                    label="Exclude whales"
                />
            </Stack>

            {(whales?.count ?? 0) > 0 && (
                <Typography variant="body2" color="text.secondary">
                    {whales?.count} whale account(s)
                    {excludeWhales || whales?.excluded ? ' excluded' : ' included'}
                    {' · '}
                    {formatBc(whales?.total_staked)} BC staked
                    {whales?.threshold ? ` (threshold ${formatBc(whales.threshold)} BC)` : ''}
                </Typography>
            )}

            {overview && (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Overview</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={2}>
                        <Kpi label="Total bets" value={String(overview.total_bets)} />
                        <Kpi label="Bettors" value={String(overview.bettors)} />
                        <Kpi label="Single / Multi" value={`${overview.single_bets} / ${overview.multi_bets}`} />
                        <Kpi label="Total staked" value={`${formatBc(overview.total_placed)} BC`} />
                        <Kpi label="Win rate" value={formatPct(overview.win_rate_pct)} />
                        <Kpi
                            label="Players net"
                            value={`${formatSignedBc(overview.players_net)} BC`}
                            emphasize
                        />
                        <Kpi label="Won / Lost / Void / Pending" value={`${overview.won} / ${overview.lost} / ${overview.void ?? 0} / ${overview.pending}`} />
                    </Stack>
                </Box>
            )}

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Awards</Typography>
                <Stack direction="row" flexWrap="wrap" gap={1.5}>
                    <AwardCard
                        title="King of the Match"
                        name={king?.name ?? '–'}
                        photoUrl={king?.photo_url}
                        detail={
                            king
                                ? `${formatSignedBc(king.profit)} BC · ${king.won}/${king.bets} won · ${formatPct(king.roi_pct)} ROI`
                                : 'No data'
                        }
                    />
                    <AwardCard
                        title="Best ROI"
                        name={bestRoi?.name ?? '–'}
                        photoUrl={bestRoi?.photo_url}
                        betName={bestRoi?.bet_type}
                        detail={
                            bestRoi
                                ? `${formatPct(bestRoi.roi_pct)} ROI · ${formatSignedBc(bestRoi.profit)} BC on ${formatBc(bestRoi.placed)} staked`
                                : 'No data'
                        }
                    />
                    <AwardCard
                        title="Biggest Single Win"
                        name={biggestWin?.name ?? '–'}
                        photoUrl={biggestWin?.photo_url}
                        betName={biggestWin?.bet_type}
                        detail={
                            biggestWin
                                ? `${formatBc(biggestWin.payout)} BC payout on ${formatBc(biggestWin.stake)} stake @ ${formatOdds(biggestWin.odds)}`
                                : 'No data'
                        }
                    />
                    <AwardCard
                        title="Longest-Odds Winner"
                        name={longestOdds?.name ?? '–'}
                        photoUrl={longestOdds?.photo_url}
                        betName={longestOdds?.bet_type}
                        detail={
                            longestOdds
                                ? `@ ${formatOdds(longestOdds.odds)}`
                                : 'No data'
                        }
                    />
                </Stack>
            </Box>

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Leaderboard (single-leg)</Typography>
                {leaderboard.length === 0 ? (
                    <EmptyState message="No leaderboard data" />
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell align="right">Bets</TableCell>
                                <TableCell align="right">Won</TableCell>
                                <TableCell align="right">Placed</TableCell>
                                <TableCell align="right">Profit</TableCell>
                                <TableCell align="right">ROI</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {leaderboard.map((row: MatchBettingLeaderboardEntryDto, index: number) => (
                                <TableRow key={row.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <UserNameWithAvatar name={row.name ?? String(row.id)} photoUrl={row.photo_url} />
                                    </TableCell>
                                    <TableCell align="right">{row.bets}</TableCell>
                                    <TableCell align="right">{row.won}</TableCell>
                                    <TableCell align="right">{formatBc(row.placed)}</TableCell>
                                    <TableCell align="right">{formatSignedBc(row.profit)}</TableCell>
                                    <TableCell align="right">{formatPct(row.roi_pct)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Most Popular Bets</Typography>
                {popularBets.length === 0 ? (
                    <EmptyState message="No popular bets data" />
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Bet</TableCell>
                                <TableCell align="right">Backed</TableCell>
                                <TableCell align="right">Avg odds</TableCell>
                                <TableCell align="right">Won</TableCell>
                                <TableCell align="right">Lost</TableCell>
                                <TableCell align="right">Hit %</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {popularBets.map((bet, index) => (
                                <TableRow key={`${bet.bet_type}-${bet.period}-${bet.game_type}-${index}`}>
                                    <TableCell>{popularBetLabel(bet)}</TableCell>
                                    <TableCell align="right">{bet.backed}</TableCell>
                                    <TableCell align="right">{formatOdds(bet.avg_odds)}</TableCell>
                                    <TableCell align="right">{bet.won}</TableCell>
                                    <TableCell align="right">{bet.lost}</TableCell>
                                    <TableCell align="right">{formatPct(bet.hit_pct)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>

            <Typography variant="caption" color="text.secondary">
                {stats.note
                    ?? 'Leaderboards use single-leg bets only. Best ROI requires at least 5,000 BC staked.'}
                {stats.compute_ms != null ? ` · compute ${stats.compute_ms} ms` : ''}
            </Typography>
        </Stack>
    );
}
